<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="isDialogOpen" max-width="600px">
    <v-tabs v-model="tab" class="d-none" />

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card color="surface lighten-1" class="main-card">
          <v-card-title>
            <div>
              <div class="xtium-title">{{ $t('wallboard.settings') }}</div>
            </div>
            <v-spacer />
          </v-card-title>

          <v-divider />
          <v-progress-linear v-show="isLoading" class="loading" height="2" color="primary" indeterminate />

          <div class="aic-config-body surface">
            <v-list subheader two-line flat color="surface" :rounded="false" over>
              <v-list-item-group multiple>
                <v-subheader class="group-subheader">{{ $t('wallboard.display') }}</v-subheader>

                <v-list-item :ripple="false">
                  <v-text-field
                    v-model="name"
                    type="text"
                    :label="$t('wallboard.name')"
                    class="text-sm-left"
                    required
                    :disabled="isLoading"
                  />
                </v-list-item>

                <v-subheader class="group-subheader">{{ $t('wallboard.slidesInterval') }}</v-subheader>

                <v-list-item :ripple="false">
                  <v-text-field
                    v-model="slidesInterval"
                    type="number"
                    min="3"
                    :label="$t('wallboard.seconds')"
                    class="text-sm-left"
                    required
                    :disabled="isLoading"
                  />
                </v-list-item>

                <v-subheader class="group-subheader">{{ $t('wallboard.fontSize') }}</v-subheader>
                <v-list-item :ripple="false">
                  <v-slider
                    v-model="fontSize"
                    track-color="grey"
                    min="1"
                    max="5"
                    :step="1"
                    prepend-icon="mdi-format-font-size-decrease"
                    append-icon="mdi-format-font-size-increase"
                    ticks="always"
                    tick-size="4"
                    @click:prepend="decreaseFontSize"
                    @click:append="increaseFontSize"
                  >
                  </v-slider>
                </v-list-item>

                <div v-if="!isSupervisor" class="theme">
                  <v-subheader class="group-subheader">THEME</v-subheader>
                  <v-list-item :ripple="false">
                    <v-row class="ma-8">
                      <v-select
                        v-model="selectedTheme"
                        :items="themeList"
                        item-value="_id"
                        item-text="name"
                        :label="$t('wallboard.theme')"
                        class="mr-6 theme-select"
                        :loading="loadingThemes"
                        @change="changeTheme"
                      />
                      <v-switch
                        v-model="themeMode"
                        class="theme-toggle"
                        :label="$t('wallboard.darkMode')"
                        @change="changeThemeMode"
                      />
                    </v-row>
                  </v-list-item>
                </div>
              </v-list-item-group>
            </v-list>

            <v-list subheader two-line flat color="surface" :rounded="false" over>
              <v-subheader class="group-subheader">{{ $t('wallboard.cards') }}</v-subheader>
              <v-list-item-group multiple>
                <v-list-item v-for="(card, index) in cards" ref="cards" :key="index">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ card.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon @click="toggleCardVisibility(card._id)">
                        <v-icon v-if="card.show">mdi-eye</v-icon>
                        <v-icon v-if="!card.show">mdi-eye-off</v-icon>
                      </v-btn>
                    </v-list-item-action>
                    <v-list-item-action>
                      <v-btn icon @click="removeCard(card._id || card.tempId)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>

                <v-list-item @click="tab = 1">
                  <template>
                    <v-list-item-action>
                      <v-icon left>mdi-plus</v-icon>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>{{ $t('wallboard.addNewCard') }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>

          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancel">
              <span v-once>{{ $t('wallboard.close') }}</span>
            </v-btn>
            <v-btn
              :loading="isLoading"
              :disabled="isLoading || !hasBeenModified"
              color="primary"
              depressed
              @click="save"
            >
              <span v-once>{{ $t('wallboard.save') }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card color="surface lighten-1">
          <v-card-title>
            <div v-once class="xtium-title">{{ $t('wallboard.cardSelection') }}</div>
          </v-card-title>

          <v-divider />

          <div class="aic-add-card-body pa-4 surface">
            <div class="customization-form">
              <v-select
                v-model="cardType"
                :items="availableCards"
                item-value="componentName"
                :item-text="item => $t('cards.' + item.title)"
                :label="$t('wallboard.cardType')"
              ></v-select>
            </div>
          </div>

          <v-divider />
          <v-spacer />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="discard">
              <span v-once>{{ $t('wallboard.close') }}</span>
            </v-btn>
            <v-btn color="primary" depressed :disabled="cardType === ''" @click="addCard">
              <span v-once>{{ $t('wallboard.create') }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import ThemeUtility from '@/components/ecs-theme/ecs-theme-utility';

const WALLBOARD_SETTINGS_UPDATED_EVENT = 'wallboard-settings-updated';
const WALLBOARD_SETTINGS_CANCELLED_EVENT = 'wallboard-settings-cancelled';
const MAX_FONT_SIZE = 5;
const MIN_FONT_SIZE = 1;

export default {
  name: 'EcsWallboardConfigDialog',

  components: {},

  props: {
    dashboardId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      fontSize: 1,
      slidesInterval: 3,
      name: 'Wallboard',
      cards: {},
      isDialogOpen: false,
      cardType: '',
      isLoading: false,
      tab: 0,
      initialSetup: null,
      selectedTheme: '',
      themeMode: false,
      themeList: [],
      loadingThemes: false
    };
  },

  computed: {
    ...mapGetters('WALLBOARD', [
      'getWallboardCards',
      'getWallboardStatistics',
      'getSlidesInterval',
      'getWallboardFontSize',
      'getWallboardTheme',
      'getWallboardName'
    ]),

    ...mapGetters('AGENT', ['isSupervisor']),

    ...mapGetters('SETTINGS', ['supervisorWallboardSettings']),

    ...mapGetters('SUPERVISOR', ['selectedSupervisorWallboard']),

    availableCards() {
      let possibleCards = [
        {
          title: 'avgSpeedAns',
          componentName: 'EcsAverageSpeedAnswerSlide'
        },
        {
          title: 'serviceLvl',
          componentName: 'EcsServiceLevelSlide'
        },
        {
          title: 'agentsStates',
          componentName: 'EcsAgentsStatesSlide'
        },
        {
          title: 'queuedInteractions',
          componentName: 'EcsInteractionsSlide'
        },
        {
          title: 'avgRingTm',
          componentName: 'EcsAverageRingTimeSlide'
        },
        {
          title: 'lngstWaitTm',
          componentName: 'EcsLongestWaitingTimeSlide'
        },
        {
          title: 'cbs',
          componentName: 'EcsCallbacksSlide'
        }
      ];
      if (this.dashboardId) {
        possibleCards = [
          ...possibleCards,
          {
            title: 'supervisedAgents',
            componentName: 'EcsSupervisedAgentsSlide'
          },
          {
            title: 'supervisedInteractions',
            componentName: 'EcsSupervisedInteractionsSlide'
          }
        ];
      }
      return possibleCards;
    },

    newCard() {
      const name = this.$t('cards.' + this.availableCards.find(card => card.componentName === this.cardType).title);

      const defaultSlide = {
        name: 'Slide 1',
        businessProcesses: [this.defaultBusinessProcesses.businessProcessId]
      };

      let defaultSizeRatio = 'Small';

      if (this.cardType === 'EcsServiceLevelSlide') {
        defaultSlide.graphThreshold = [20, 80];
      }

      if (this.cardType === 'EcsAgentsStatesSlide') {
        defaultSlide.states = ['Ready'];
        defaultSlide.showAsGraph = true;
        defaultSlide.showAgentNames = false;
      }

      if (this.cardType === 'EcsCallbacksSlide') {
        defaultSlide.callbacks = ['Processed'];
        defaultSlide.showAsGraph = true;
      }

      if (this.cardType === 'EcsInteractionsSlide') {
        defaultSlide.interactionsTypeList = ['Overflow', 'Answered', 'Abandoned', 'Waiting'];
        defaultSlide.showAsGraph = true;
      }

      if (
        this.cardType === 'EcsInteractionsSlide' ||
        this.cardType === 'EcsAverageSpeedAnswerSlide' ||
        this.cardType === 'EcsLongestWaitingTimeSlide'
      ) {
        defaultSlide.mediaTypes = ['Telephony', 'Chat', 'SMS'];
      }

      if (this.cardType === 'EcsSupervisedAgentsSlide' || this.cardType === 'EcsSupervisedInteractionsSlide') {
        defaultSizeRatio = 'Medium';
      }

      const defaultCard = {
        tempId: uuidv4(),
        name,
        show: true,
        componentName: this.cardType,
        slides: defaultSlide,
        sizeRatio: defaultSizeRatio
      };

      return defaultCard;
    },

    defaultBusinessProcesses() {
      return this.getWallboardStatistics[0];
    },

    hasBeenModified() {
      if (this.initialSetup) {
        if (this.initialSetup.fontSize !== this.fontSize) {
          return true;
        }
        if (Number(this.initialSetup.slidesInterval) !== Number(this.slidesInterval)) {
          return true;
        }
        if (this.initialSetup.name !== this.name) {
          return true;
        }
        if (JSON.stringify(this.initialSetup.cards) !== JSON.stringify(this.cards)) {
          return true;
        }
        if (this.initialSetup.selectedTheme !== this.selectedTheme) {
          return true;
        }
        if (this.initialSetup.themeMode !== this.themeMode) {
          return true;
        }
      }
      return false;
    }
  },
  mounted() {
    this.cards = this.getWallboardCards;
    this.retrieveInitialData();
  },
  methods: {
    ...mapActions('WALLBOARD', ['updateWallboard']),

    ...mapActions('SETTINGS', ['updateCurrentSupervisorWallboardSettings']),

    addCard() {
      this.tab = 0;
      this.cards = [...this.cards, this.newCard];
    },

    getCardComponentName(componentName) {
      const card = this.availableCards.find(c => c.componentName === componentName);
      return card.title;
    },

    toggleCardVisibility(cardId) {
      this.cards = this.cards.map(card => {
        if (card._id === cardId) {
          return { ...card, show: !card.show };
        }

        return card;
      });
    },

    removeCard(cardId) {
      this.cards = this.cards.filter(card => card._id !== cardId && card.tempId !== cardId);
    },

    discard() {
      this.tab = 0;
      this.cardType = '';
    },

    cancel() {
      this.name = this.initialSetup.name;
      this.fontSize = this.initialSetup.fontSize;
      this.slidesInterval = this.initialSetup.slidesInterval;
      this.isDialogOpen = false;
      this.cards = this.initialSetup.wallboardCards;
      this.selectedTheme = this.initialSetup.selectedTheme;
      this.themeMode = this.initialSetup.themeMode;

      const theme = { _id: this.selectedTheme, mode: this.themeMode ? 'dark' : 'light' };

      if (!this.isSupervisor) {
        ThemeUtility.build(theme);
      }
    },

    save() {
      this.isDialogOpen = false;

      const payload = {};
      if (this.initialSetup.name !== this.name) {
        payload.name = this.name;
      }
      if (Number(this.initialSetup.slidesInterval) !== Number(this.slidesInterval)) {
        payload.intervalSeconds = this.slidesInterval;
      }
      if (this.initialSetup.fontSize !== this.fontSize) {
        payload.fontSize = this.fontSize;
      }
      if (JSON.stringify(this.initialSetup.cards) !== JSON.stringify(this.cards)) {
        payload.cards = this.cards;
      }
      if (this.initialSetup.selectedTheme !== this.selectedTheme || this.initialSetup.themeMode !== this.themeMode) {
        const theme = { _id: this.selectedTheme, mode: this.themeMode ? 'dark' : 'light' };
        payload.theme = theme;
      }

      this.isLoading = true;
      this.updateWallboard({ ...payload, dashboardId: this.dashboardId }).finally(() => {
        if (this.isSupervisor && payload.name) {
          const wallboards = this.supervisorWallboardSettings.wallboards;

          let updatedWallboards = null;
          updatedWallboards = wallboards.map(wallboard => {
            if (this.selectedSupervisorWallboard._id === wallboard._id) {
              wallboard.name = payload.name;
            }
            return wallboard;
          });

          this.updateCurrentSupervisorWallboardSettings({ wallboards: updatedWallboards });
        }
        this.isLoading = false;
      });
    },

    /** This is used to update the local values from the server */
    open() {
      this.slidesInterval = this.getSlidesInterval;
      this.fontSize = this.getWallboardFontSize;
      this.name = this.getWallboardName;
      this.cards = this.getWallboardCards;
      const theme = this.getWallboardTheme || { _id: '', mode: 'light' };
      this.selectedTheme = theme._id;
      this.themeMode = theme.mode === 'dark';
      this.isDialogOpen = true;

      this.initialSetup = {
        fontSize: this.fontSize,
        slidesInterval: this.slidesInterval,
        name: this.name,
        cards: this.cards,
        selectedTheme: this.selectedTheme,
        themeMode: this.themeMode
      };

      return new Promise((resolve, reject) => {
        this.$once(WALLBOARD_SETTINGS_UPDATED_EVENT, resolve);
        this.$once(WALLBOARD_SETTINGS_CANCELLED_EVENT, reject);
      });
    },

    increaseFontSize() {
      if (this.fontSize >= MAX_FONT_SIZE) {
        return;
      }

      this.fontSize = this.fontSize + 1;
    },

    decreaseFontSize() {
      if (this.fontSize <= MIN_FONT_SIZE) {
        return;
      }

      this.fontSize = this.fontSize - 1;
    },

    ...mapActions('SETTINGS', ['updateThemeMode', 'updateThemeId']),

    changeThemeMode(themeMode) {
      themeMode ? ThemeUtility.setDarkMode() : ThemeUtility.setLightMode();
    },

    changeTheme(themeId) {
      const theme = this.themeList.find(theme => theme._id === themeId);

      ThemeUtility.setTheme(theme);
    },

    retrieveInitialData() {
      this.loadingThemes = true;
      ThemeUtility.getAllThemes()
        .then(async themes => {
          this.themeList = themes || [];
          await this.$nextTick();
        })
        .catch(console.log)
        .finally(() => {
          this.loadingThemes = false;
        });
    }
  }
};
</script>

<style scoped>
.main-card {
  border-radius: 0 !important;
}
.aic-config-body,
.aic-add-card-body {
  height: 400px;
  overflow-y: auto;
}
.v-list-item:hover {
  background-color: var(--v-hover-background);
}
.form-group {
  padding: 8px 0 20px 32px;
}
.form-group-title {
  height: 36px;
  font-size: 12px;
}
.group-subheader {
  color: var(--v-primary-base);
  margin: 0 0 -8px 0;
}
.dashboard-attributes {
  margin-top: 20px;
}
.loading {
  position: absolute;
}
</style>
